import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import QuoteCommentsPanel from '../components/QuoteCommentsPanel';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { CheckCircle2, Send, Calendar, User, FileText, ClipboardCheck, Search, FileSpreadsheet, Filter } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AFMValidationPage() {
  const { hasAnyRole } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [sentDialogOpen, setSentDialogOpen] = useState(false);
  const [receivedDialogOpen, setReceivedDialogOpen] = useState(false);
  const [sentDate, setSentDate] = useState('');
  const [receivedDate, setReceivedDate] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [markingJms, setMarkingJms] = useState(null); // Track which quote is being marked
  
  // Check if user has read-only access (Project Head or Key Account Manager)
  const isReadOnly = hasAnyRole(['Project Head', 'Key Account Manager']) && !hasAnyRole(['Super Admin', 'Billing Manager']);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  
  // Filter states
  const [actionFilter, setActionFilter] = useState('all');
  const [jmsFilter, setJmsFilter] = useState('all');
  
  // Export states
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportFromDate, setExportFromDate] = useState('');
  const [exportToDate, setExportToDate] = useState('');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchQuotes();
  }, []);

  const fetchQuotes = async () => {
    try {
      const response = await axios.get(`${API}/quotes/afm-validation`);
      setQuotes(response.data);
    } catch (error) {
      toast.error('Failed to load quotes');
    } finally {
      setLoading(false);
    }
  };

  const openSentDialog = (quote) => {
    setSelectedQuote(quote);
    setSentDate(new Date().toISOString().split('T')[0]);
    setSentDialogOpen(true);
  };

  const openReceivedDialog = (quote) => {
    setSelectedQuote(quote);
    setReceivedDate(new Date().toISOString().split('T')[0]);
    setReceivedDialogOpen(true);
  };

  const handleMarkAsSent = async () => {
    if (!sentDate) {
      toast.error('Please provide the date when email was sent');
      return;
    }

    setSubmitting(true);
    try {
      await axios.put(`${API}/quotes/${selectedQuote.id}/afm-validation`, {
        afm_validation_sent: true,
        afm_validation_sent_date: sentDate
      });
      toast.success('Marked as sent for AFM validation');
      setSentDialogOpen(false);
      fetchQuotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateReceivedDate = async () => {
    if (!receivedDate) {
      toast.error('Please provide the validation received date');
      return;
    }

    setSubmitting(true);
    try {
      await axios.put(`${API}/quotes/${selectedQuote.id}/afm-validation`, {
        afm_validation_received_date: receivedDate
      });
      toast.success('Validation received date updated');
      setReceivedDialogOpen(false);
      fetchQuotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    } finally {
      setSubmitting(false);
    }
  };

  const handleMarkReadyForJms = async (quote) => {
    setMarkingJms(quote.id);
    try {
      await axios.put(`${API}/quotes/${quote.id}/ready-for-jms`);
      toast.success('Marked as Ready for JMS');
      fetchQuotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    } finally {
      setMarkingJms(null);
    }
  };

  // Export to Excel handler
  const handleExportToExcel = async () => {
    if (!exportFromDate || !exportToDate) {
      toast.error('Please select both From and To dates');
      return;
    }
    
    if (new Date(exportFromDate) > new Date(exportToDate)) {
      toast.error('From date cannot be after To date');
      return;
    }
    
    setExporting(true);
    try {
      const response = await axios.get(
        `${API}/quotes/afm-validation/export?from_date=${exportFromDate}&to_date=${exportToDate}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `AFM_Validation_${exportFromDate}_to_${exportToDate}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Excel file downloaded successfully');
      setExportDialogOpen(false);
      setExportFromDate('');
      setExportToDate('');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  // Helper function to get action status for a quote
  const getActionStatus = (quote) => {
    if (!quote.afm_validation_sent) {
      return 'pending_sent'; // Needs "Mark Sent"
    } else if (quote.afm_validation_received_date) {
      return 'received'; // Already received
    } else {
      return 'pending_received'; // Needs "Update Received"
    }
  };

  // Helper function to get JMS status for a quote
  const getJmsStatus = (quote) => {
    if (quote.ready_for_jms_date) {
      return 'yes'; // Already marked as Yes with date
    } else if (quote.afm_validation_received_date) {
      return 'pending'; // Can be marked as Yes
    } else {
      return 'no'; // Not ready (no validation received)
    }
  };

  // Filter quotes based on search query and filters
  const filteredQuotes = quotes.filter(quote => {
    // Search filter
    if (searchQuery && !quote.quote_no?.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    
    // Action filter
    if (actionFilter !== 'all') {
      const actionStatus = getActionStatus(quote);
      if (actionFilter !== actionStatus) {
        return false;
      }
    }
    
    // JMS filter
    if (jmsFilter !== 'all') {
      const jmsStatus = getJmsStatus(quote);
      if (jmsFilter !== jmsStatus) {
        return false;
      }
    }
    
    return true;
  });

  if (loading) {
    return (
      <Sidebar>
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-lg">Loading quotes...</div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="space-y-6" data-testid="afm-validation-page">
        <div className="flex flex-col sm:flex-row justify-between gap-4">
          <div>
            <h1 className="text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              AFM Validation
            </h1>
            <p className="text-muted-foreground mt-2">
              {filteredQuotes.length} quote(s) found
              {searchQuery && ` (filtered from ${quotes.length})`}
            </p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => setExportDialogOpen(true)}
              data-testid="export-excel-btn"
            >
              <FileSpreadsheet className="mr-2 h-4 w-4" />
              Export to Excel
            </Button>
          </div>
        </div>

        {/* Search Box and Filters */}
        <div className="flex flex-wrap gap-4 items-end">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search by Quote Number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-quote-input"
            />
          </div>
          
          {/* Action Filter */}
          <div className="min-w-[180px]">
            <Label className="text-xs text-muted-foreground mb-1 block">Actions Filter</Label>
            <Select value={actionFilter} onValueChange={setActionFilter}>
              <SelectTrigger data-testid="action-filter">
                <SelectValue placeholder="All Actions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                <SelectItem value="pending_sent">Mark Sent (Pending)</SelectItem>
                <SelectItem value="pending_received">Update Received (Pending)</SelectItem>
                <SelectItem value="received">Received (Completed)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Ready for JMS Filter */}
          <div className="min-w-[180px]">
            <Label className="text-xs text-muted-foreground mb-1 block">Ready for JMS Filter</Label>
            <Select value={jmsFilter} onValueChange={setJmsFilter}>
              <SelectTrigger data-testid="jms-filter">
                <SelectValue placeholder="All JMS Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All JMS Status</SelectItem>
                <SelectItem value="yes">Yes (Completed)</SelectItem>
                <SelectItem value="pending">Mark as Yes (Pending)</SelectItem>
                <SelectItem value="no">No</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Clear Filters Button */}
          {(actionFilter !== 'all' || jmsFilter !== 'all') && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => {
                setActionFilter('all');
                setJmsFilter('all');
              }}
              className="h-10"
            >
              Clear Filters
            </Button>
          )}
        </div>

        {filteredQuotes.length === 0 ? (
          <Card className="p-12 text-center glass-card">
            <p className="text-muted-foreground">
              {searchQuery ? `No quotes found for "${searchQuery}"` : 'No approved quotes for AFM validation'}
            </p>
          </Card>
        ) : (
          <Card className="overflow-hidden glass-table">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#1B273D] text-white">
                    <th className="px-4 py-3 text-left text-sm font-semibold">Quote No</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Job Sheet Status</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Sent for AFM Validation</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">AFM Name</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Validation Received Date</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">Actions</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">Ready for JMS</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredQuotes.map((quote) => (
                    <tr key={quote.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center">
                          <span className="font-mono font-medium text-[#DC143C]">{quote.quote_no}</span>
                          <QuoteCommentsPanel quoteId={quote.id} quoteNo={quote.quote_no} />
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircle2 className="h-3 w-3" />
                          Yes
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {quote.afm_validation_sent ? (
                          <div>
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              <Send className="h-3 w-3" />
                              Yes
                            </span>
                            {quote.afm_validation_sent_date && (
                              <p className="text-xs text-gray-500 mt-1">
                                Sent: {quote.afm_validation_sent_date}
                              </p>
                            )}
                          </div>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                            No
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-gray-400" />
                          <span className="text-sm">{quote.afm_name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {quote.afm_validation_received_date ? (
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-green-500" />
                            <span className="text-sm font-medium text-green-700">
                              {quote.afm_validation_received_date}
                            </span>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {isReadOnly ? (
                          <div className="flex justify-center">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                              View Only
                            </span>
                          </div>
                        ) : (
                        <div className="flex justify-center gap-2">
                          {!quote.afm_validation_sent ? (
                            <Button
                              size="sm"
                              onClick={() => openSentDialog(quote)}
                              className="bg-[#DC143C] hover:bg-[#b91130]"
                              data-testid={`mark-sent-${quote.id}`}
                            >
                              <Send className="h-3 w-3 mr-1" />
                              Mark Sent
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openReceivedDialog(quote)}
                              disabled={!!quote.afm_validation_received_date}
                              data-testid={`update-received-${quote.id}`}
                            >
                              <Calendar className="h-3 w-3 mr-1" />
                              {quote.afm_validation_received_date ? 'Received' : 'Update Received'}
                            </Button>
                          )}
                        </div>
                        )}
                      </td>
                      {/* Ready for JMS Column */}
                      <td className="px-4 py-3">
                        <div className="flex justify-center">
                          {quote.ready_for_jms_date ? (
                            <div className="flex items-center gap-2">
                              <CheckCircle2 className="h-4 w-4 text-green-500" />
                              <span className="text-sm font-medium text-green-700">
                                {quote.ready_for_jms_date}
                              </span>
                            </div>
                          ) : quote.afm_validation_received_date && !isReadOnly ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleMarkReadyForJms(quote)}
                              disabled={markingJms === quote.id}
                              className="border-green-500 text-green-600 hover:bg-green-50"
                              data-testid={`mark-jms-${quote.id}`}
                            >
                              <ClipboardCheck className="h-3 w-3 mr-1" />
                              {markingJms === quote.id ? 'Marking...' : 'Mark as Yes'}
                            </Button>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                              No
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>

      {/* Mark as Sent Dialog */}
      <Dialog open={sentDialogOpen} onOpenChange={setSentDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Mark as Sent for AFM Validation</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">
                Quote: <span className="font-medium font-mono">{selectedQuote?.quote_no}</span>
              </p>
              <p className="text-sm text-gray-600 mt-1">
                AFM: <span className="font-medium">{selectedQuote?.afm_name}</span>
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="sent_date">Date Email Sent *</Label>
              <Input
                id="sent_date"
                type="date"
                value={sentDate}
                onChange={(e) => setSentDate(e.target.value)}
                required
              />
              <p className="text-xs text-muted-foreground">
                Enter the date when you sent the email to AFM
              </p>
            </div>
            
            <div className="flex gap-2">
              <Button
                onClick={handleMarkAsSent}
                disabled={submitting || !sentDate}
                className="flex-1 bg-[#DC143C] hover:bg-[#b91130]"
              >
                {submitting ? 'Saving...' : 'Confirm'}
              </Button>
              <Button variant="outline" onClick={() => setSentDialogOpen(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Update Received Date Dialog */}
      <Dialog open={receivedDialogOpen} onOpenChange={setReceivedDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Update Validation Received Date</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">
                Quote: <span className="font-medium font-mono">{selectedQuote?.quote_no}</span>
              </p>
              <p className="text-sm text-gray-600 mt-1">
                AFM: <span className="font-medium">{selectedQuote?.afm_name}</span>
              </p>
              {selectedQuote?.afm_validation_sent_date && (
                <p className="text-sm text-gray-600 mt-1">
                  Sent on: <span className="font-medium">{selectedQuote.afm_validation_sent_date}</span>
                </p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="received_date">Validation Received Date *</Label>
              <Input
                id="received_date"
                type="date"
                value={receivedDate}
                onChange={(e) => setReceivedDate(e.target.value)}
                required
              />
              <p className="text-xs text-muted-foreground">
                Enter the date when you received the validation response from AFM
              </p>
            </div>
            
            <div className="flex gap-2">
              <Button
                onClick={handleUpdateReceivedDate}
                disabled={submitting || !receivedDate}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                {submitting ? 'Saving...' : 'Update'}
              </Button>
              <Button variant="outline" onClick={() => setReceivedDialogOpen(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Export to Excel Dialog */}
      <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="export-dialog">
          <DialogHeader>
            <DialogTitle>Export to Excel</DialogTitle>
            <DialogDescription>
              Select a date range (Quotation Date) to export AFM validation data
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="export_from_date">From Date (Quotation Date) *</Label>
              <Input
                id="export_from_date"
                type="date"
                value={exportFromDate}
                onChange={(e) => setExportFromDate(e.target.value)}
                data-testid="export-from-date"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="export_to_date">To Date (Quotation Date) *</Label>
              <Input
                id="export_to_date"
                type="date"
                value={exportToDate}
                onChange={(e) => setExportToDate(e.target.value)}
                data-testid="export-to-date"
              />
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handleExportToExcel}
                disabled={exporting || !exportFromDate || !exportToDate}
                className="flex-1"
                data-testid="confirm-export-btn"
              >
                <FileSpreadsheet className="mr-2 h-4 w-4" />
                {exporting ? 'Exporting...' : 'Export'}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setExportDialogOpen(false);
                  setExportFromDate('');
                  setExportToDate('');
                }}
                disabled={exporting}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Sidebar>
  );
}
